import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { AppLoaderService } from 'src/app/services/app-loader/app-loader.service';
import { ReportsService } from 'src/app/services/reports.service';

@Component({
  selector: 'app-contractor-dashboard',
  templateUrl: './contractor-dashboard.component.html',
  styleUrls: ['./contractor-dashboard.component.scss'],
})
export class ContractorDashboardComponent implements OnInit {
  // Data and options for Total Applied Area Chart
  public totalAreaChartData: any;
  public totalAreaChartOptions: any;

  // Data and options for Count of Applications Chart
  public countAppsChartData: any;
  public countAppsChartOptions: any;

  public miDistrictWiseData: any = [];
  public epcTableData: any = [];

  constructor(
    private reportsService: ReportsService,
    private loader: AppLoaderService,
    private messageService: MessageService,
  ) { }

  ngOnInit(): void {
    this.loadAllData();
  }

  /**
   * Loads both MI District Wise Data and EPC Table Data simultaneously.
   */
  loadAllData() {
    this.loader.start();

    forkJoin({
      miData: this.reportsService.GetEPCReports(),
      epcData: this.reportsService.getEpcFullData(),
    }).subscribe({
      next: ({ miData, epcData }) => {
        this.loader.close();

        // Process MI District Wise Data
        if (miData?.length > 0) {
          this.miDistrictWiseData = miData;
          this.prepareTotalAreaChartData();
        } else {
          this.miDistrictWiseData = [];
          this.prepareTotalAreaChartData();
          this.messageService.add({
            severity: 'info',
            summary: 'No Data',
            detail: 'MI District Wise Data Not Available',
          });
        }

        // Process EPC Table Data
        if (Array.isArray(epcData)) {
          this.epcTableData = epcData;
        } else {
          this.epcTableData = [];
        }

        // Prepare Count of Applications Chart
        this.prepareCountAppsChartData();
      },
      error: (err) => {
        this.loader.close();
        this.miDistrictWiseData = [];
        this.epcTableData = [];
        this.prepareTotalAreaChartData();
        this.prepareCountAppsChartData();

        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data.',
        });
      }
    });
  }

  /**
   * Prepares data and options for the Total Applied Area Chart.
   */
  prepareTotalAreaChartData() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    // Mapping districts and total area applied (null values handled explicitly)
    const districts = this.miDistrictWiseData.map((item) => item.DistrictName || item.Outlets);
    const totalAreaApplied = this.miDistrictWiseData.map((item) => item.Total_Area_Applied !== null ? item.Total_Area_Applied : 0); // Explicitly handle null values

    this.totalAreaChartData = {
      labels: districts,
      datasets: [
        {
          label: 'Total Area Applied',
          backgroundColor: '#42A5F5',
          data: totalAreaApplied, // Use the mapped total area data
        }
      ]
    };

    // this.totalAreaChartOptions = {
    //   maintainAspectRatio: false,
    //   aspectRatio: 0.8,
    //   plugins: {
    //     datalabels: {
    //       anchor: 'end',
    //       align: 'end',
    //       color: 'black',
    //       offset: -7,
    //       font: {
    //         weight: 'bold',
    //         size: '10px'
    //       },
    //     },
    //     legend: {
    //       display: false // Hide the legend
    //     },
    //     tooltip: {
    //       callbacks: {
    //         label: function (tooltipItem) {
    //           const index = tooltipItem.dataIndex;
    //           return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
    //         }
    //       }
    //     }
    //   },
    //   scales: {
    //     x: {
    //       ticks: {
    //         color: 'black',
    //         font: {
    //           weight: 'bold'
    //         },
    //       },
    //       grid: {
    //         color: surfaceBorder,
    //         drawBorder: false
    //       }
    //     },
    //     y: {
    //       type: 'linear', // Use linear scale
    //       ticks: {
    //         min: 0,
    //         // stepSize: 10,
    //         color: textColor,
    //         font: {
    //           weight: 'bold',
    //           color: 'black'
    //         },
    //         beginAtZero: true
    //       },
    //       grid: {
    //         display: false,
    //       }
    //     },
    //   }
    // };

    this.totalAreaChartOptions = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          display: true,
          labels: {
            color: textColor
          }
        },
        tooltip: {
          callbacks: {
            label: function (tooltipItem: any) {
              return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
            font: {
              weight: 'bold'
            }
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        // y: {
        //   ticks: {
        //     color: textColor,
        //     font: {
        //       weight: 'bold'
        //     },
        //     beginAtZero: true  // Ensure y-axis starts from 0
        //   },
        //   grid: {
        //     color: surfaceBorder
        //   }
        // }
        y: {
          type: 'linear', // Use linear scale
          ticks: {
            min: 0,
            // stepSize: 10,
            color: textColor,
            font: {
              weight: 'bold',
              color: 'black'
            },
            beginAtZero: true
          },
          grid: {
            display: false,
          }
        },
      }
    };
  }

  /**
   * Prepares data and options for the Count of Applications Chart.
   */
  prepareCountAppsChartData() {
    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    const districts = this.miDistrictWiseData.map((item) => item.DistrictName || item.Outlets);

    // Calculate count of applications per district
    const countApps = districts.map((district) => {
      return this.epcTableData.filter((app: any) => app.DistrictName === district).length;
    });

    this.countAppsChartData = {
      labels: districts,
      datasets: [
        {
          label: 'Count of Applications',
          backgroundColor: '#FF6384',
          data: countApps,
        }
      ]
    };


    this.countAppsChartOptions = {
      maintainAspectRatio: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          display: true,
          labels: {
            color: textColor
          }
        },
        tooltip: {
          callbacks: {
            label: function (tooltipItem: any) {
              return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: textColor,
            font: {
              weight: 'bold'
            }
          },
          grid: {
            color: surfaceBorder,
            drawBorder: false
          }
        },
        // y: {
        //   ticks: {
        //     color: textColor,
        //     font: {
        //       weight: 'bold'
        //     },
        //     beginAtZero: true  // Ensure y-axis starts from 0
        //   },
        //   grid: {
        //     color: surfaceBorder
        //   }
        // }
        y: {
          type: 'linear', // Use linear scale
          ticks: {
            min: 0,
            // stepSize: 10,
            color: textColor,
            font: {
              weight: 'bold',
              color: 'black'
            },
            beginAtZero: true
          },
          grid: {
            display: false,
          }
        },
      }
    };

    // this.countAppsChartOptions = {
    //   maintainAspectRatio: false,
    //   aspectRatio: 0.8,
    //   plugins: {
    //     legend: {
    //       display: true,
    //       labels: {
    //         color: textColor
    //       }
    //     },
    //     tooltip: {
    //       callbacks: {
    //         label: function (tooltipItem: any) {
    //           return `${tooltipItem.dataset.label}: ${tooltipItem.raw}`;
    //         }
    //       }
    //     }
    //   },
    //   scales: {
    //     x: {
    //       ticks: {
    //         color: textColor,
    //         font: {
    //           weight: 'bold'
    //         }
    //       },
    //       grid: {
    //         color: surfaceBorder,
    //         drawBorder: false
    //       }
    //     },
    //     y: {
    //       ticks: {
    //         color: textColor,
    //         font: {
    //           weight: 'bold'
    //         },
    //         beginAtZero: true
    //       },
    //       grid: {
    //         color: surfaceBorder
    //       }
    //     }
    //   }
    // };
  }
}
